import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { Subscription } from "rxjs";

import { Coordinates } from "@hermes/api-model-common";
import { Shop } from "@hermes/api-model-shop";
import { Context, LOCALE } from "@hermes/app-core";
import { isChina, Locale } from "@hermes/locale";
import { Logger } from "@hermes/logger";
import { GeoService, getDirectionUrl } from "@hermes/map-utils";
import { getCoordinatesBasedOnLocale } from "@hermes/shop-utils";
import { LOGGER } from "@hermes/web-logger";

import { ShopInfoEvent } from "../../types/shop-info.type";

export type ShopInfoDirectionMode = "icon" | "text";

@Component({
    selector: "h-shop-info-direction",
    templateUrl: "./shop-info-direction.component.html",
    styleUrls: ["./shop-info-direction.component.scss"],
    standalone: false,
})
export class ShopInfoDirectionComponent implements OnInit, OnDestroy {
    @Input()
    public shop!: Shop;

    /**
     * Number of meters between the current location and the shop
     * If meters > 0, the number of km is displayed, next to the address
     */
    @Input()
    public meters: number = 0;

    /**
     * Sets the store title level
     */
    @Input()
    public headerLevelShopTitle: number = 3;

    /**
     * Direction URL to the store
     */
    @Input()
    public directionUrl!: string;

    /**
     * Mode used to display the direction information : text or icon
     */
    @Input()
    public mode: ShopInfoDirectionMode = "text";

    @Output()
    public shopInfoDirectionEvents = new EventEmitter<ShopInfoEvent>();

    public subscription: Subscription = new Subscription();

    public directionInMetersId = "";

    public directionTextLinkId = "";

    public shopNameId = "";

    constructor(
        public context: Context,
        private geoService: GeoService,
        @Inject(LOGGER) private logger: Logger,
        @Inject(LOCALE) private locale: Locale,
    ) {}

    public ngOnInit(): void {
        this.directionInMetersId = `distance-in-meters-${this.shop.shopId}`;
        this.directionTextLinkId = `direction-text-link-${this.shop.shopId}`;
        this.shopNameId = `shop-name-${this.shop.shopId}`;
        // If the direction link is not already set by the @Input, we have to calculate it
        if (this.directionUrl) {
            return;
        }
        if (this.context.isInBrowserMode()) {
            this.directionUrlManagement();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Returns true if a store is closer than 1km from the
     * researched location.
     *
     * @param meters numbers of meters to store
     */
    public isCloserThan1Km(meters: number): boolean {
        return meters < 1000;
    }

    private directionUrlManagement() {
        // Init direction link without user's position
        this.directionUrl = getDirectionUrl(this.locale)(
            getCoordinatesBasedOnLocale(this.shop, this.locale),
        );

        this.subscription.add(
            this.geoService.getCurrentPosition().subscribe({
                next: (position) => {
                    const coord = new Coordinates({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                    this.directionUrl = getDirectionUrl(this.locale)(
                        isChina(this.locale)
                            ? this.shop.baiduGeoCoordinates
                            : this.shop.geoCoordinates,
                        coord,
                    );
                },
                error: () => {
                    // Non-blocking error : User position is not mandatory
                    this.logger.info("Current position not available for user");
                },
            }),
        );
    }
}
